<script setup lang="ts">
import { computed, onMounted, PropType, ref } from "vue";
import { InterventionSchedule } from "@/types/planning";
import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInput,
    IonIcon,
    IonTextarea,
    IonTitle,
    IonToolbar,
    modalController,
    IonNote,
    alertController,
} from "@ionic/vue";
import { generateAudit, IIndoorFloorTrapAudit, IIndoorFlyingTrapAudit, IOutdoorTrapAudit } from "@/types/audit";
import { trash } from "ionicons/icons";
import { ICostumer } from "@/types/costumer";
import { useCostumersStore } from "@/stores/costumers";
import { IWeekModel } from "@/types/week";
import CostumerToIntevSched from "@/components/modals/SelectCustomer.vue";
import { format } from "date-fns";

const props = defineProps({
    week: {
        type: Object as PropType<IWeekModel>,
        required: true,
    },
    order: {
        type: Number,
        required: false,
        default: 0,
    },
    interventionSchedule: {
        type: Object as PropType<InterventionSchedule>,
        required: false,
    },
});
const calcletInitialScheduleDate = () => {
    if (props.interventionSchedule?.scheduled_date) {
        return props.interventionSchedule.scheduled_date;
    }
    const startDate = new Date(props.week.start);
    return format(startDate, "yyyy-MM-dd");
};
const costumersStore = useCostumersStore();
const schedule = ref({
    id: props.interventionSchedule?.id ?? -1,
    customer: props.interventionSchedule?.customer,
    contract: props.interventionSchedule?.contract,
    scheduled_week: props.interventionSchedule?.scheduled_week ?? props.week?.week,
    scheduled_date: calcletInitialScheduleDate(),
    order: props.interventionSchedule?.order ?? props.order,
    // Set fixed to true here so if users save the form, it will always be saved as fixed
    fixed: true,
    label: props.interventionSchedule?.label ?? "",
    note: props.interventionSchedule?.note ?? "",
});

const selectedCostumer = computed(() => {
    if (schedule.value.customer) {
        return costumersStore.getCustomerById(schedule.value.customer);
    }
    return null;
});

const checkWeek = () => {
    // Check if schedule.value.scheduled_date is within the props.week
    const scheduledDate = new Date(schedule.value.scheduled_date);
    const weekStart = new Date(props.week.start);
    const weekEnd = new Date(props.week.end);
    return scheduledDate >= weekStart && scheduledDate <= weekEnd;
};
const cancel = () => {
    return modalController.dismiss(null, "cancel");
};
const save = async () => {
    if (!checkWeek()) {
        const alert = await alertController.create({
            header: "Fuori dalla settimana",
            message: `La data selezionata è al di fuori della settimana selezionata. Vuoi continuare?`,
            buttons: [
                {
                    text: "Annulla",
                    role: "cancel",
                    cssClass: "ion-text-medium",
                },
                {
                    text: "Sovrascrivi",
                    cssClass: "ion-text-danger",
                    handler: async () => {
                        return modalController.dismiss(schedule.value, "confirm");
                    },
                },
            ],
        });
        await alert.present();
        const selectResult = await alert.onDidDismiss();
        if (selectResult.role === "confirm") {
            // Get previous monday (or itself if it's already monday)
            const previousMonday = new Date(schedule.value.scheduled_date);
            previousMonday.setDate(previousMonday.getDate() - ((previousMonday.getDay() + 6) % 7));
            schedule.value.scheduled_week = format(previousMonday, "yyyy-MM-dd");
            return modalController.dismiss(schedule.value, "confirm");
        }
        return;
    }

    return modalController.dismiss(schedule.value, "confirm");
};

const formValid = computed(() => true);

const isOpeningCustomerModal = ref(false);
const openCostumerModal = async () => {
    if (isOpeningCustomerModal.value) return;
    isOpeningCustomerModal.value = true;
    const modal = await modalController.create({
        component: CostumerToIntevSched,
        componentProps: {},
        cssClass: "modal-costumer",
    });
    await modal.present();
    try {
        const results = await modal.onDidDismiss();
        if (results.role === "confirm") {
            schedule.value.customer = (results.data as ICostumer).id;
        }
    } finally {
        isOpeningCustomerModal.value = false;
    }
};

const deleteSchedule = () => {
    return modalController.dismiss(null, "delete");
};

onMounted(() => {});
</script>

<template>
    <ion-header>
        <ion-toolbar>
            <ion-buttons slot="start">
                <ion-button color="medium" @click="cancel">Annulla</ion-button>
            </ion-buttons>
            <ion-title>
                <span v-if="schedule.id > 0" class="font-bold">Modifica pianificazione</span>
                <span v-else class="font-bold">Aggiungi una pianificazione</span>
            </ion-title>

            <ion-buttons slot="end">
                <ion-button :disabled="!formValid" color="primary" :strong="true" @click="save()">Salva</ion-button>
            </ion-buttons>
        </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding" style="--background: var(--ion-item-background)">
        <form class="">
            <ion-input
                label="Cliente"
                label-placement="stacked"
                :value="selectedCostumer?.business_name"
                :readonly="true"
                @click.stop="openCostumerModal()"
                class="mei-input mei-note-input ion-margin-top"></ion-input>
            <ion-input
                class="mei-input mei-note-input ion-margin-top"
                label="Etichetta"
                v-model="schedule.label"
                label-placement="stacked"></ion-input>
            <ion-input
                type="date"
                label="Data"
                label-placement="stacked"
                class="mei-input ion-margin-top"
                v-model="schedule.scheduled_date"></ion-input>
            <ion-textarea
                class="mei-input mei-note-input ion-margin-top"
                v-model="schedule.note"
                label="Note"
                style="height: 150px"
                label-placement="stacked"
                tabindex="-1"></ion-textarea>
            <ion-note color="medium" class="block ion-margin-top-double ion-text-end">
                Ordine: {{ schedule.order > 0 ? schedule.order : "Primo" }}
            </ion-note>
        </form>
        <div
            slot="fixed"
            v-if="schedule.id > 0"
            style="position: absolute; bottom: 0; left: 0; right: 0"
            class="ion-padding">
            <ion-button expand="block" color="danger" :disabled="!formValid" :strong="true" @click="deleteSchedule()">
                <ion-icon slot="start" :icon="trash" />
                Elimina
            </ion-button>
        </div>
    </ion-content>
</template>

<style scoped></style>
